/**
 * Rebuy Service Layer for Asmodee UK B2B Store
 * Shared API wrapper for Rebuy recommendations and SparkLayer integration.
 */

(function () {
  'use strict';

  /* ── Constants ── */

  const API_BASE = 'https://rebuyengine.com/api/v1';
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const CART_CACHE_TTL_MS = 2 * 60 * 1000;
  const MAX_RETRIES = 2;
  const RETRY_DELAY_MS = 1000;
  const ATTRIBUTION_TTL_MS = 5 * 60 * 1000;

  // productId -> { sku, attributionId, timeoutId } for adds that route through
  // SparkLayer's <spark-pdp> widget (PDP rec Quick Add), tagged later via
  // preCartUpdateListener. timeoutId is the TTL handle, cleared when superseded.
  const pendingAttribution = new Map();

  /* ── Cache ── */

  const _cache = new Map();

  function cacheGet(key) {
    const entry = _cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > entry.ttl) {
      _cache.delete(key);
      return null;
    }
    return entry.data;
  }

  function cacheSet(key, data, ttl) {
    _cache.set(key, { data, timestamp: Date.now(), ttl });
  }

  function cacheClear() {
    _cache.clear();
  }

  /* ── Debug instrumentation ── */

  function debugEnabled() {
    return !!(window.__rebuyConfig && window.__rebuyConfig.debug);
  }

  function perfMark(name) {
    if (!debugEnabled() || !window.performance || !performance.mark) return;
    performance.mark(name);
  }

  function perfMeasure(label, startMark, endMark) {
    if (!debugEnabled() || !window.performance || !performance.measure) return null;
    try {
      // User Timing L3 returns the entry directly; L2 returns undefined and we
      // pull the most recent entry by name as a fallback.
      const measure = performance.measure(label, startMark, endMark);
      if (measure && measure.duration != null) return measure.duration;
      const entries = performance.getEntriesByName(label);
      return entries.length ? entries[entries.length - 1].duration : null;
    } catch (err) {
      return null;
    }
  }

  // Per-fetch counter so concurrent fetches in the same context get unique
  // mark names rather than racing on a shared 'rebuy:fetch-pdp-start' pair.
  let fetchSeq = 0;

  /* ── API helpers ── */

  function getApiKey() {
    return window.Rebuy?.shop?.api_key || window.__rebuyConfig?.apiKey || '';
  }

  async function fetchWithRetry(url, retries) {
    retries = retries != null ? retries : MAX_RETRIES;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Rebuy API ' + response.status);
      }
      return await response.json();
    } catch (err) {
      if (retries > 0) {
        await new Promise(function (resolve) { setTimeout(resolve, RETRY_DELAY_MS); });
        return fetchWithRetry(url, retries - 1);
      }
      throw err;
    }
  }

  /* ── RebuyService ── */

  let _readyPromise = null;

  const RebuyService = {

    ready: false,

    init: function () {
      if (_readyPromise) return _readyPromise;

      _readyPromise = new Promise(function (resolve) {
        let attempts = 0;
        const maxAttempts = 150;

        function check() {
          const apiKey = getApiKey();
          if (window.Rebuy && window.Rebuy._status === 'initialized' && apiKey) {
            RebuyService.ready = true;
            document.dispatchEvent(new CustomEvent('rebuy-service:ready'));
            resolve();
          } else {
            attempts++;
            if (attempts >= maxAttempts) {
              console.warn('[RebuyService] Timed out waiting for Rebuy SDK');
              RebuyService.ready = true;
              document.dispatchEvent(new CustomEvent('rebuy-service:ready'));
              resolve();
            } else {
              setTimeout(check, 200);
            }
          }
        }
        check();
      });

      return _readyPromise;
    },

    /* ── Recommendations ── */

    getRecommendations: async function (options) {
      options = options || {};
      await this.init();

      const apiKey = getApiKey();
      if (!apiKey) {
        console.warn('[RebuyService] No API key available');
        return [];
      }

      const limit = options.limit || 4;
      const context = options.context || 'pdp';
      const ttl = context === 'cart' ? CART_CACHE_TTL_MS : CACHE_TTL_MS;

      const params = ['key=' + encodeURIComponent(apiKey), 'limit=' + limit];

      if (options.productId) {
        params.push('shopify_product_ids=' + options.productId);
      } else if (options.productIds && options.productIds.length) {
        params.push('shopify_product_ids=' + options.productIds.join(','));
      }

      const customer = window.__rebuyCustomer;
      if (customer && customer.id) {
        params.push('shopify_customer_id=' + customer.id);
      }

      const endpoint = options.dataSourceId
        ? '/custom/id/' + encodeURIComponent(options.dataSourceId)
        : '/products/recommended';
      const url = API_BASE + endpoint + '?' + params.join('&');

      const cacheKey = 'recs:' + url;
      const cached = cacheGet(cacheKey);
      if (cached) {
        if (debugEnabled()) {
          console.log('[RebuyService] cache hit (' + context + ')');
        }
        return cached;
      }

      // Context + sequence so concurrent same-context fetches (e.g. two PDP
      // rec blocks) get distinct mark names and accurate per-fetch durations.
      const fetchId = ++fetchSeq;
      const markBase = 'rebuy:fetch-' + context + '-' + fetchId;
      const markStart = markBase + '-start';
      const markEnd = markBase + '-end';
      perfMark(markStart);
      try {
        const response = await fetchWithRetry(url);
        perfMark(markEnd);
        const duration = perfMeasure(markBase, markStart, markEnd);
        if (duration != null) {
          console.log('[RebuyService] API ' + context + ' fetch: ' + duration.toFixed(1) + 'ms');
        }
        const products = response.data || [];
        cacheSet(cacheKey, products, ttl);
        return products;
      } catch (err) {
        console.error('[RebuyService] Failed to fetch recommendations:', err);
        return [];
      }
    },

    /* ── Cart-recs prefetch ── */

    // Warms the recommendations cache so a subsequent drawer-open hits the
    // cache instead of paying the API round-trip. Invoked from SparkLayer's
    // onCartUpdate callback on every cart commit. SparkLayer passes the full
    // cart; falls back to getCart() when called without an argument.
    prefetchCartRecommendations: async function (cart) {
      const config = window.__rebuyConfig;
      if (!config) return;

      if (!cart) cart = await this.getCart();
      const cartItems = cart && cart.items ? cart.items : [];
      const cartProductIds = cartItems
        .map(function (item) { return item.parentExternalId || item.externalId; })
        .filter(Boolean);

      const options = {
        limit: (config.cartLimit || 3) + cartItems.length,
        context: 'cart'
      };
      if (config.cartDataSourceId) options.dataSourceId = config.cartDataSourceId;
      if (cartProductIds.length) options.productIds = cartProductIds;

      try {
        await this.getRecommendations(options);
      } catch (err) {
        // Prefetch is best-effort; failure is not user-facing.
      }
    },

    /* ── SparkLayer integration ── */

    addToCart: async function (sku, quantity, source) {
      if (!window.spark || typeof window.spark.updateCart !== 'function') {
        console.warn('[RebuyService] spark.updateCart not available');
        return [];
      }

      quantity = quantity || 1;
      source = source || 'rebuy';

      const payload = {
        products: [
          {
            sku: sku,
            adjustQuantity: quantity,
            customAttributes: [
              { key: '_source', value: 'Rebuy' },
              { key: '_attribution', value: source }
            ]
          }
        ],
        clearCart: false
      };

      try {
        const result = await window.spark.updateCart(payload);

        // Invalidate cart recommendation cache on cart change
        _cache.forEach(function (value, key) {
          if (key.indexOf('recs:') === 0 && key.indexOf('cart') !== -1) {
            _cache.delete(key);
          }
        });

        return result;
      } catch (err) {
        console.error('[RebuyService] addToCart failed:', err);
        return [];
      }
    },

    getCart: async function () {
      if (!window.spark || typeof window.spark.getCart !== 'function') {
        return null;
      }
      try {
        return await window.spark.getCart();
      } catch (err) {
        console.warn('[RebuyService] getCart failed:', err);
        return null;
      }
    },

    /* ── Tracking ── */

    trackProductView: function (product) {
      if (!product || !product.id) return;
      try {
        if (window.Rebuy && window.Rebuy.util && window.Rebuy.util.RequestTracker) {
          window.Rebuy.util.RequestTracker.send('product_view', {
            product_id: product.id
          });
        }
      } catch (err) {
        // Tracking failure is non-critical
      }
    },

    trackAddToCart: function (product, variantId) {
      if (!product || !product.id) return;
      try {
        if (window.Rebuy && window.Rebuy.util && window.Rebuy.util.RequestTracker) {
          window.Rebuy.util.RequestTracker.send('add_to_cart', {
            product_id: product.id,
            variant_id: variantId
          });
        }
      } catch (err) {
        // Tracking failure is non-critical
      }
    },

    /* ── Attribution ── */

    registerPendingAttribution: function (productId, sku, attributionId) {
      if (!attributionId || !productId) return;
      const key = String(productId);

      const existing = pendingAttribution.get(key);
      if (existing && existing.timeoutId) clearTimeout(existing.timeoutId);

      const timeoutId = setTimeout(function () {
        pendingAttribution.delete(key);
      }, ATTRIBUTION_TTL_MS);

      pendingAttribution.set(key, { sku: sku || '', attributionId: attributionId, timeoutId: timeoutId });
    },

    applyPendingAttribution: function (cart) {
      if (!cart || !cart.products || !pendingAttribution.size) return cart;

      cart.products.forEach(function (product) {
        if (!product.customAttributes) product.customAttributes = [];
        const attributes = product.customAttributes;

        const alreadyTagged = attributes.some(function (attr) {
          return attr.key === '_source';
        });
        if (alreadyTagged) return;

        let matchedKey = null;
        if (product.parentExternalId && pendingAttribution.has(String(product.parentExternalId))) {
          matchedKey = String(product.parentExternalId);
        } else if (product.sku) {
          for (const [key, entry] of pendingAttribution) {
            if (entry.sku && entry.sku === product.sku) {
              matchedKey = key;
              break;
            }
          }
        }
        if (!matchedKey) return;

        const entry = pendingAttribution.get(matchedKey);
        attributes.push({ key: '_source', value: 'Rebuy' });
        attributes.push({ key: '_attribution', value: entry.attributionId });
        if (entry.timeoutId) clearTimeout(entry.timeoutId);
        pendingAttribution.delete(matchedKey);
      });

      return cart;
    },

    /* ── Utilities ── */

    clearCache: cacheClear,

    filterCartItems: function (products, cartItems) {
      if (!cartItems || !cartItems.length) return products;

      const cartSkus = {};
      cartItems.forEach(function (item) {
        if (item.sku) cartSkus[item.sku] = true;
      });

      return products.filter(function (product) {
        if (!product.variants || !product.variants.length) return true;
        return !product.variants.some(function (variant) {
          return variant.sku && cartSkus[variant.sku];
        });
      });
    },

    escapeHTML: function (str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    getImageUrl: function (src, width) {
      if (!src) return '';
      width = width || 300;
      if (src.indexOf('cdn.shopify.com') !== -1) {
        const separator = src.indexOf('?') !== -1 ? '&' : '?';
        return src + separator + 'width=' + width;
      }
      return src;
    },

    formatMoney: function (cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const formatString = format || '{{amount}}';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = formatString.match(placeholderRegex);

      if (!match) return (cents / 100).toFixed(2);

      let value;
      switch (match[1]) {
        case 'amount':
          value = (cents / 100).toFixed(2);
          break;
        case 'amount_no_decimals':
          value = Math.round(cents / 100).toString();
          break;
        case 'amount_with_comma_separator':
          value = (cents / 100).toFixed(2).replace('.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = Math.round(cents / 100).toString().replace('.', ',');
          break;
        default:
          value = (cents / 100).toFixed(2);
      }
      return formatString.replace(placeholderRegex, value);
    },

    /**
     * Extract common product data from a Rebuy product object.
     * Normalises the variant/image/URL fields both card builders need.
     */
    extractProductData: function (product) {
      const variant = product.variants && product.variants[0];
      let primaryImage = '';

      if (product.image && product.image.src) {
        primaryImage = product.image.src;
      } else if (product.images && product.images.length && product.images[0].src) {
        primaryImage = product.images[0].src;
      }

      return {
        variant: variant || null,
        sku: variant ? variant.sku || '' : '',
        variantId: variant ? variant.id || '' : '',
        primaryImage: primaryImage,
        productUrl: product.handle ? '/products/' + encodeURIComponent(product.handle) : '#'
      };
    },

    /**
     * Format a variant's RRP price for display.
     * Returns empty string if variant or price is missing.
     */
    formatRRP: function (variant, moneyFormat) {
      if (!variant || !variant.compare_at_price) return '';
      const cents = parseFloat(variant.compare_at_price) * 100;
      return this.formatMoney(cents, moneyFormat);
    },

  };

  /* ── Expose globally ── */

  window.RebuyService = RebuyService;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      RebuyService.init();
    });
  } else {
    RebuyService.init();
  }
})();
